import React from 'react';
import { useSearchParams } from 'react-router-dom'; // Import useSearchParams from react-router-dom
import { Clock, CalendarDays, Repeat, EllipsisVertical } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Batch } from '@/types';
import { Button } from '@/components/ui/button';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from '@tanstack/react-table';
import { DoubleArrowLeftIcon, DoubleArrowRightIcon } from '@radix-ui/react-icons';

interface Props {
    columns: [];
    data: Batch[];
    handleViewDetails: (batchId: string) => void;
    setSelectedBatchId: React.Dispatch<React.SetStateAction<string | null>>;
    pageCount: number;
    pageSizeOptions?: number[];
}

const BatchCardComponent: React.FC<Props> = ({
    columns,
    data,
    handleViewDetails,
    setSelectedBatchId,
    pageCount,
    pageSizeOptions = [10, 20, 30, 40, 50],
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [sorting, setSorting] = React.useState<SortingState>([]);
    // Search params
    const page = searchParams?.get('page') ?? '1';
    const pageAsNumber = Number(page);
    const fallbackPage = isNaN(pageAsNumber) || pageAsNumber < 1 ? 1 : pageAsNumber;
    const per_page = searchParams?.get('limit') ?? 50;
    const perPageAsNumber = Number(per_page);
    const fallbackPerPage = isNaN(perPageAsNumber) ? 50 : perPageAsNumber;
    // Get the current page and page size from the search params or set defaults
    const pageParam = searchParams.get('page') || '1';
    // Convert page and limit to numbers
    const currentPage = parseInt(pageParam) - 1; // 0-based index for internal use

    const handlePageChange = (newPage: number) => {
        if (newPage >= 0 && newPage < pageCount) {
            setSearchParams({
                page: (newPage + 1).toString(), // 1-based for URL
                limit: pageSize.toString(),
            });
        }
    };
    const [{ pageIndex, pageSize }, setPagination] = React.useState({
        pageIndex: fallbackPage - 1,
        pageSize: fallbackPerPage,
    });
    React.useEffect(() => {
        const defaultSortOrder =
            sorting.length > 0
                ? sorting[0]?.desc
                    ? 'DESC'
                    : 'ASC'
                : searchParams.get('sortOrder') ?? 'ASC';

        // Update the URL with the new page number and limit
        setSearchParams({
            ...Object.fromEntries(searchParams), // Spread the existing search params
            page: (pageIndex + 1).toString(), // Update the page number (assuming pageIndex is 0-based)
            limit: pageSize.toString(), // Update the limit
            sortBy: sorting[0]?.id ?? searchParams.get('sortBy') ?? 'updated_at',
            sortOrder: defaultSortOrder,
            // sortOrder: sorting[0]?.desc ? 'DESC' : 'ASC' ?? searchParams.get('sortOrder') ?? 'DESC',
        });
    }, [pageIndex, pageSize, sorting, searchParams, setSearchParams]);
    const table = useReactTable({
        data,
        columns,
        pageCount: pageCount ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            pagination: { pageIndex, pageSize },
            sorting,
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        manualPagination: true,
        manualFiltering: true,
    });

    return (
        <div>
            <ScrollArea className="h-[calc(100vh-14rem)] overflow-auto bg-muted/40">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 m-2">
                    {data?.map((batch) => (
                        <Card
                            key={batch.id}
                            className="relative shadow-md transition-transform transform hover:scale-105 hover:shadow-md duration-300" // Added classes here
                        >
                            <div className="absolute top-2 right-2">
                                <DropdownMenu>
                                    <DropdownMenuTrigger>
                                        <EllipsisVertical />
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent side="bottom" align="end">
                                        <DropdownMenuItem
                                            onClick={() => handleViewDetails(batch?.id)}
                                            className="cursor-pointer">
                                            View Details
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            onClick={() => setSelectedBatchId(batch?.id)}
                                            className="cursor-pointer">
                                            Delete Batch
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                            <CardHeader className="bg-muted p-2 mb-2">
                                <CardTitle className="text-sm font-semibold text-center ">
                                    <span
                                        className="cursor-pointer"
                                        onClick={() => handleViewDetails(batch?.id)}>
                                        {batch.batch_code}
                                    </span>
                                </CardTitle>
                            </CardHeader>
                            <CardDescription className="flex ml-6 ">
                                <Badge
                                    className="mr-2"
                                    style={{
                                        backgroundColor: batch?.batch_stream?.some(
                                            (stream) => stream.stream_names === 'JEE'
                                        )
                                            ? '#e76e50' // Color for JEE
                                            : batch?.batch_stream?.some(
                                                    (stream) => stream.stream_names === 'Medical'
                                                )
                                              ? '#2a9d90' // Color for Medical
                                              : 'transparent', // Default background if neither
                                    }}>
                                    <div className="flex items-center">
                                        <p className="text-xs">
                                            {batch?.batch_stream?.map((batchStream: any) => (
                                                <span key={batchStream.stream_names}>
                                                    {batchStream.stream_names
                                                        ? batchStream.stream_names
                                                        : ''}
                                                </span>
                                            ))}
                                        </p>
                                    </div>
                                </Badge>
                                <Badge className="mr-2 bg-white text-black border border-cyan-500 hover:bg-white">
                                    <div className="flex items-center">
                                        <p className="text-xs">
                                            {batch.locations?.map((loc) => loc.name).join(', ')}
                                        </p>
                                    </div>
                                </Badge>
                                <Badge className="mr-2 bg-white text-black border border-pink-600 hover:bg-white">
                                    <div className="flex items-center">
                                        <p className="text-xs">{batch?.batch_types[0]?.name}</p>
                                    </div>
                                </Badge>
                            </CardDescription>
                            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3  ">
                                <div className="flex items-center ">
                                    <CalendarDays size="16" className="text-primary  mr-2" />
                                    <p className="text-sm">{batch.starting_date}</p>
                                </div>
                                <div className="flex items-center">
                                    <Clock size="16" className="text-primary mr-2" />
                                    <p className="text-sm">
                                        {batch?.duration_type === 'Years' && (
                                            <>
                                                {batch?.duration}
                                                {batch?.duration === 1 ? ' Year' : ' Years'}
                                            </>
                                        )}
                                        {batch?.duration_type &&
                                            batch?.duration_type !== 'Years' && (
                                                <span>
                                                    {batch?.duration + ' ' + batch.duration_type}
                                                    {/* {batch.duration_type} */}
                                                </span>
                                            )}
                                    </p>
                                </div>

                                <div className="flex items-center col-span-2 ">
                                    <Repeat size="16" className="text-primary mr-2" />
                                    <p className="text-sm">
                                        {batch?.selected_days_count ? 'Flexible' : 'Fixed'}
                                        <span className="ml-2">
                                            {batch?.selected_days_count
                                                ? batch?.selected_days_count + ' Days'
                                                : Array.isArray(batch.selected_days)
                                                  ? batch.selected_days
                                                        .map((days: any) => days)
                                                        .join(', ')
                                                  : batch.selected_days}
                                        </span>
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
                <ScrollBar orientation="horizontal" />
            </ScrollArea>

            <div className="flex mt-4">
                <div className="flex flex-col items-center gap-4 sm:flex-row sm:gap-6 lg:gap-8">
                    <div className="flex items-center space-x-2">
                        <p className="whitespace-nowrap text-sm font-medium">Rows per page</p>

                        <Select
                            value={`${table.getState().pagination.pageSize}`}
                            onValueChange={(value: string) => {
                                table.setPageSize(Number(value));
                            }}>
                            <SelectTrigger className="h-8 w-[70px]">
                                <SelectValue placeholder={table.getState().pagination.pageSize} />
                            </SelectTrigger>
                            <SelectContent side="top">
                                {pageSizeOptions.map((pSize) => (
                                    <SelectItem key={pSize} value={`${pSize}`}>
                                        {pSize}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="flex w-full items-center justify-between gap-2 sm:justify-end">
                    <div className="flex w-[100px] items-center justify-center text-sm font-medium">
                        {/* Page {currentPage + 1} of {pageCount} */}
                        Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                    </div>
                    <div className="flex items-center space-x-2">
                        <Button
                            aria-label="Go to first page"
                            variant="outline"
                            className="hidden h-8 w-8 p-0 lg:flex"
                            onClick={() => table.setPageIndex(0)}
                            disabled={!table.getCanPreviousPage()}>
                            <DoubleArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
                        </Button>
                        <Button
                            aria-label="Go to previous page"
                            variant="outline"
                            className="h-8 w-8 p-0"
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}>
                            <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
                        </Button>
                        <Button
                            aria-label="Go to next page"
                            variant="outline"
                            className="h-8 w-8 p-0"
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}>
                            <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
                        </Button>
                        <Button
                            aria-label="Go to last page"
                            variant="outline"
                            className="hidden h-8 w-8 p-0 lg:flex"
                            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                            disabled={!table.getCanNextPage()}>
                            <DoubleArrowRightIcon className="h-4 w-4" aria-hidden="true" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BatchCardComponent;
